import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { leavesAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import AccessibleModal from '../components/common/AccessibleModal';

const LEAVE_TYPES = ['vacation', 'sick', 'personal', 'unpaid', 'other'];

const LeaveModal = ({ onClose, onSave, t }) => {
  const [form, setForm] = useState({ startDate: '', endDate: '', leaveType: 'vacation', reason: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await leavesAPI.create(form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally { setLoading(false); }
  };

  return (
    <AccessibleModal
      title={`🌴 ${t('leaves.requestLeave')}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" form="leave-create-form" className="btn btn-primary" disabled={loading}>{loading ? t('common.loading') : t('common.save')}</button>
        </>
      )}
    >
        <form id="leave-create-form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-error" role="alert">⚠ {error}</div>}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('leaves.startDate')} *</label>
                <input type="date" className="form-input" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">{t('leaves.endDate')} *</label>
                <input type="date" className="form-input" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('leaves.type')} *</label>
              <select className="form-select" value={form.leaveType} onChange={e => setForm(f => ({ ...f, leaveType: e.target.value }))}>
                {LEAVE_TYPES.map(lt => <option key={lt} value={lt}>{t(`leaves.leaveTypes.${lt}`)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('leaves.reason')}</label>
              <textarea className="form-textarea" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={3} />
            </div>
        </form>
    </AccessibleModal>
  );
};

const ReviewModal = ({ leave, onClose, onSave, t }) => {
  const [form, setForm] = useState({ status: 'approved', reviewNote: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await leavesAPI.review(leave.id, form);
      onSave();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <AccessibleModal
      title={`📋 ${leave.first_name} ${leave.last_name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" form="leave-review-form" className={`btn ${form.status === 'approved' ? 'btn-success' : 'btn-danger'}`} disabled={loading}>
            {loading ? t('common.loading') : (form.status === 'approved' ? t('leaves.approve') : t('leaves.reject'))}
          </button>
        </>
      )}
    >
        <form id="leave-review-form" onSubmit={handleSubmit}>
            <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: 16, marginBottom: 20 }}>
              <div className="flex justify-between mb-2"><span className="text-muted">{t('leaves.type')}</span><strong>{t(`leaves.leaveTypes.${leave.leave_type}`)}</strong></div>
              <div className="flex justify-between mb-2"><span className="text-muted">{t('leaves.startDate')}</span><strong>{leave.start_date}</strong></div>
              <div className="flex justify-between"><span className="text-muted">{t('leaves.endDate')}</span><strong>{leave.end_date}</strong></div>
              {leave.reason && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--gray-600)' }}>{leave.reason}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('leaves.status')}</label>
              <select className="form-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="approved">{t('leaves.approved')}</option>
                <option value="rejected">{t('leaves.rejected')}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('leaves.reviewNote')}</label>
              <textarea className="form-textarea" value={form.reviewNote} onChange={e => setForm(f => ({ ...f, reviewNote: e.target.value }))} rows={2} />
            </div>
        </form>
    </AccessibleModal>
  );
};

const LeaveDetailsModal = ({ leave, onClose, t }) => (
  <AccessibleModal
    title="📄 Détails de la demande"
    onClose={onClose}
    footer={<button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.close')}</button>}
  >
        <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: 16 }}>
          <div className="flex justify-between mb-2"><span className="text-muted">{t('staff.firstName')} / {t('staff.lastName')}</span><strong>{leave.first_name} {leave.last_name}</strong></div>
          <div className="flex justify-between mb-2"><span className="text-muted">{t('leaves.type')}</span><strong>{t(`leaves.leaveTypes.${leave.leave_type}`)}</strong></div>
          <div className="flex justify-between mb-2"><span className="text-muted">{t('leaves.startDate')}</span><strong>{leave.start_date}</strong></div>
          <div className="flex justify-between mb-2"><span className="text-muted">{t('leaves.endDate')}</span><strong>{leave.end_date}</strong></div>
          <div className="flex justify-between mb-2"><span className="text-muted">{t('leaves.status')}</span><strong>{t(`leaves.${leave.status}`)}</strong></div>
          <div className="flex justify-between"><span className="text-muted">{t('common.createdAt')}</span><strong>{format(new Date(leave.created_at), 'dd/MM/yyyy HH:mm')}</strong></div>
          {leave.reason && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--gray-600)' }}>{leave.reason}</div>}
          {leave.review_note && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--gray-600)' }}><strong>{t('leaves.reviewNote')}:</strong> {leave.review_note}</div>}
        </div>
  </AccessibleModal>
);

const LeavesPage = () => {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [reviewLeave, setReviewLeave] = useState(null);
  const [detailsLeave, setDetailsLeave] = useState(null);
  const [openMenuLeaveId, setOpenMenuLeaveId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await leavesAPI.getAll({ status: statusFilter });
      setLeaves(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => {
    const closeMenu = () => setOpenMenuLeaveId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const statusBadge = (s) => {
    const map = { pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-danger' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{t(`leaves.${s}`)}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{t('leaves.title')}</h2>
          <p className="text-muted">{leaves.length} {t('common.all').toLowerCase()}</p>
        </div>
        {hasRole('admin', 'manager', 'staff') && (
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ {t('leaves.requestLeave')}</button>
        )}
      </div>

      <div className="toolbar">
        <select className="form-select" style={{ width: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">{t('common.all')}</option>
          <option value="pending">{t('leaves.pending')}</option>
          <option value="approved">{t('leaves.approved')}</option>
          <option value="rejected">{t('leaves.rejected')}</option>
        </select>
      </div>

      <div className="card">
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t('staff.firstName')} / {t('staff.lastName')}</th>
                  <th>{t('leaves.type')}</th>
                  <th>{t('leaves.startDate')}</th>
                  <th>{t('leaves.endDate')}</th>
                  <th>{t('leaves.status')}</th>
                  <th>{t('common.createdAt')}</th>
                  {hasRole('admin', 'manager') && <th>{t('common.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {leaves.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>{t('leaves.noLeaves')}</td></tr>
                )}
                {leaves.map(lv => (
                  <tr key={lv.id}>
                    <td style={{ fontWeight: 600 }}>{lv.first_name} {lv.last_name}</td>
                    <td>{t(`leaves.leaveTypes.${lv.leave_type}`)}</td>
                    <td>{lv.start_date}</td>
                    <td>{lv.end_date}</td>
                    <td>{statusBadge(lv.status)}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{format(new Date(lv.created_at), 'dd/MM/yy')}</td>
                    {hasRole('admin', 'manager') && (
                      <td>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuLeaveId(curr => (curr === lv.id ? null : lv.id));
                            }}
                            aria-label="Ouvrir le menu d'actions"
                            aria-haspopup="menu"
                            aria-expanded={openMenuLeaveId === lv.id}
                          >
                            ⋮
                          </button>
                          {openMenuLeaveId === lv.id && (
                            <div
                              role="menu"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute',
                                top: '110%',
                                right: 0,
                                minWidth: 180,
                                background: 'white',
                                border: '1px solid var(--gray-200)',
                                borderRadius: 8,
                                boxShadow: 'var(--shadow-md)',
                                zIndex: 20,
                                padding: 6,
                              }}
                            >
                              <button
                                className="btn btn-sm btn-outline"
                                style={{ width: '100%', justifyContent: 'flex-start', marginBottom: lv.status === 'pending' ? 6 : 0 }}
                                onClick={() => {
                                  setDetailsLeave(lv);
                                  setOpenMenuLeaveId(null);
                                }}
                              >
                                Consulter
                              </button>
                              {lv.status === 'pending' && (
                                <button
                                  className="btn btn-sm btn-outline"
                                  style={{ width: '100%', justifyContent: 'flex-start' }}
                                  onClick={() => {
                                    setReviewLeave(lv);
                                    setOpenMenuLeaveId(null);
                                  }}
                                >
                                  Traiter
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showAdd && <LeaveModal onClose={() => setShowAdd(false)} onSave={() => { setShowAdd(false); load(); }} t={t} />}
      {reviewLeave && <ReviewModal leave={reviewLeave} onClose={() => setReviewLeave(null)} onSave={() => { setReviewLeave(null); load(); }} t={t} />}
      {detailsLeave && <LeaveDetailsModal leave={detailsLeave} onClose={() => setDetailsLeave(null)} t={t} />}
    </div>
  );
};

export default LeavesPage;
